import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchQuestsToday, claimQuest, type QuestView } from "../api/quests.api";

function formatTokens(s: string): string {
  return Number(s).toLocaleString();
}

export function QuestsPanel({ onReward }: { onReward?: () => void }) {
  const [quests, setQuests] = useState<QuestView[]>([]);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchQuestsToday();
      setQuests(data.quests);
      setStreak(data.streak);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "failed to load quests");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleClaim = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await claimQuest(id);
      await refresh();
      onReward?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "claim failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="quests-panel">
      <div className="quests-header">
        <h2>Daily Quests</h2>
        <span className="streak-badge">
          🔥 {streak.current} day{streak.current !== 1 ? "s" : ""}
        </span>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <ul className="quests-list">
        <AnimatePresence>
          {quests.map((q) => {
            const ready = q.progress >= q.targetProgress;
            const claimed = q.claimed;
            return (
              <motion.li
                key={q.id}
                className={`quest-row ${claimed ? "claimed" : ready ? "ready" : ""}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <div className="quest-info">
                  <div className="quest-title">{q.title}</div>
                  <div className="quest-desc">{q.description}</div>
                  <div className="quest-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(100, (q.progress / q.targetProgress) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className="progress-text">
                      {q.progress}/{q.targetProgress}
                    </span>
                  </div>
                </div>
                <div className="quest-action">
                  <div className="quest-reward">+{formatTokens(q.rewardTokens)}</div>
                  {claimed ? (
                    <span className="claimed-tag">Claimed</span>
                  ) : ready ? (
                    <button onClick={() => handleClaim(q.id)} disabled={busyId === q.id}>
                      {busyId === q.id ? "…" : "Claim"}
                    </button>
                  ) : (
                    <span className="locked-tag">In progress</span>
                  )}
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </section>
  );
}
